import React, { FC, useMemo } from 'react';
import { SkeletonCard, Card } from './product/index';
import styles from './ProductList.module.css';
import { Text, Loader } from 'ui';
import { useTranslations } from '~/services/cms';
import { IFetchRelewiseProps, RelewiseProduct } from './hooks';
import DownloadProductList from '../DownloadProductList/DownloadProductList';
import { ViewItemListTracker } from '~/tracking/GTM/components/ViewItemListTracker';
import { withTracking } from 'tracking';
import { ItemListType, PropsWithTracking } from '~/tracking/GTM/types';
import { trackSelectItem } from '~/tracking/GTM/functions/trackSelectItem';



type Props = Omit<IFetchRelewiseProps, 'activeFacets' | 'disallowedProductsGroups' | 'locale'> & {
    products?: RelewiseProduct[];
    isLoading?: boolean;
    isFetching?: boolean;
    totalProducts: number;
    headline?: string;
    selectedVehicleTypeNumber?: number | null;
    // Tracking props
    itemListId?: string;
    itemListName?: string;
    itemListType?: ItemListType;
}


const SkeletonList = () => (
    <>
        {Array.from(Array(5).keys()).map((item) => (
            <SkeletonCard key={item} />
        ))}
    </>
)

const handleTrackSelectItem = (product: RelewiseProduct, index: number, itemListName: string, itemListId: string, itemListType: ItemListType) => {
    console.log('handleTrackSelectItem called with:', { product, index, itemListName, itemListId, itemListType });
    trackSelectItem({
        product,
        index,
        itemListName,
        itemListId,
        itemListType,
    })
}

const ProductList: FC<PropsWithTracking<Props>> = ({
    products, totalProducts, isLoading, isFetching, headline,
    selectedVehicleTypeNumber, searchQuery, itemListId, itemListName, itemListType, ...rest
}) => {
    const translate = useTranslations();

    const tracking = useMemo(() => ({
        listName: itemListName ?? headline ?? 'Product List',
        listId: itemListId ?? 'product-list-page',
        listType: itemListType ?? 'ProductListPage',
    }), [itemListName, headline, itemListId, itemListType])


    return (
        <div className={styles.root}>
            <div className={styles.header}>
                <div className={styles.headerLeft}>
                    <Text variant="body" className={styles.plpTitle}>
                        {headline ? headline : translate('product.products')}
                    </Text>
                    {isFetching && <Loader size='15px' borderWidth='2px' padding='0' />}
                </div>
                <div className={styles.headerRight}>
                    <Text variant="bodySmall">
                        {totalProducts} {translate('product.results')}
                    </Text>
                    <DownloadProductList
                        totalProductsCount={totalProducts}
                        searchQuery={searchQuery}
                        {...rest}
                    />
                </div>
            </div>

            <div className={styles.productList}>
                {isLoading
                    ? <SkeletonList />
                    : products?.map((product, index) => (
                        <Card
                            searchQuery={searchQuery}
                            key={`${index}-${product?.productId}`}
                            selectedVehicleTypeNumber={selectedVehicleTypeNumber}
                            product={product}
                            tracking={{
                                onClick: (product) => handleTrackSelectItem(
                                    product,
                                    index,
                                    tracking.listName,
                                    tracking.listId,
                                    tracking.listType
                                ),
                                listId: tracking.listId,
                                listName: tracking.listName,
                                listIndex: index,
                                listType: tracking.listType
                            }}
                        />
                    ))
                }
            </div>
        </div>
    );
};

const ProductsListWithTracking = withTracking(
    ProductList,
    ViewItemListTracker,
);

export { ProductsListWithTracking as ProductList };